import React from 'react';
import {
    Box, Typography, Button, Skeleton, Alert, Stack,
} from '@mui/material';
import {
    ShoppingBag as BagIcon,
    ArrowForward as ArrowIcon,
    LocalShippingOutlined as ShipIcon,
    CheckCircleOutline as DoneIcon,
    CancelOutlined as CancelIcon,
    HourglassTopOutlined as PendingIcon,
    InfoOutlined as InfoIcon,
    TrendingUp as SpendIcon,
    ReceiptLong as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import marketplaceService from '../services/marketplaceService';

// ─── Design tokens (matches app blue) ────────────────────────────────────────
const C = {
    blue:      '#1976d2',
    blueLight: '#e3f0fc',
    blueDark:  '#1256a0',
    bg:        '#f5f7fa',
    white:     '#ffffff',
    border:    '#e0e4ea',
    text:      '#1a1d23',
    muted:     '#6b7280',
    radius:    '6px',
};

const STATUS_STYLE = {
    pending:   { label: 'Pending',   bg: '#fff8e1', color: '#f57f17', Icon: PendingIcon },
    confirmed: { label: 'Confirmed', bg: '#e3f0fc', color: '#1565c0', Icon: InfoIcon },
    shipped:   { label: 'Shipped',   bg: '#f3e5f5', color: '#7b1fa2', Icon: ShipIcon },
    delivered: { label: 'Delivered', bg: '#e8f5e9', color: '#2e7d32', Icon: DoneIcon },
    cancelled: { label: 'Cancelled', bg: '#fdecea', color: '#c62828', Icon: CancelIcon },
};

const Pill = ({ status }) => {
    const s = STATUS_STYLE[status] || { label: status, bg: '#f3f4f6', color: C.muted };
    return (
        <Box component="span" sx={{
            display: 'inline-block', px: 1.5, py: '3px', borderRadius: '20px',
            fontSize: 12, fontWeight: 600, bgcolor: s.bg, color: s.color,
            whiteSpace: 'nowrap',
        }}>
            {s.label}
        </Box>
    );
};

const StatCard = ({ icon, label, value, iconColor = C.blue, iconBg = C.blueLight }) => (
    <Box sx={{
        flex: 1, p: 2.5, bgcolor: C.white, border: `1px solid ${C.border}`,
        borderRadius: C.radius,
    }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
                <Typography sx={{ fontSize: 11, color: C.muted, mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.text }}>{value}</Typography>
            </Box>
            <Box sx={{ bgcolor: iconBg, borderRadius: '8px', p: 1.2, display: 'flex' }}>
                {React.cloneElement(icon, { sx: { fontSize: 20, color: iconColor } })}
            </Box>
        </Stack>
    </Box>
);

// ─── Purchase row ─────────────────────────────────────────────────────────────
const PurchaseRow = ({ order, onClick, componentMap, imageMap }) => {
    const firstItem = order.items?.[0];
    const mpn = firstItem
        ? (componentMap[firstItem.component_id]?.mpn || firstItem.mpn || `#${firstItem.component_id}`)
        : '—';
    const imgUrl = firstItem ? imageMap[firstItem.component_id] : null;
    const extra = (order.items?.length || 1) - 1;

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'grid',
                gridTemplateColumns: '2.5fr 1.2fr 0.8fr 1fr 1.2fr 100px',
                px: 3, py: 2, alignItems: 'center',
                cursor: 'pointer', bgcolor: C.white,
                borderBottom: `1px solid ${C.border}`,
                transition: 'background 0.12s',
                '&:hover': { bgcolor: C.bg },
            }}
        >
            {/* Purchase ID + part */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '6px', flexShrink: 0,
                    bgcolor: C.blueLight, overflow: 'hidden', border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {imgUrl ? (
                        <Box component="img" src={imgUrl} alt={mpn}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    ) : (
                        <ReceiptIcon sx={{ fontSize: 18, color: C.blue }} />
                    )}
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: C.blue, mb: 0.2 }}>
                        {order.order_id}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: C.muted }}>
                        {mpn}{extra > 0 ? ` +${extra} more` : ''}
                    </Typography>
                </Box>
            </Box>

            {/* Date */}
            <Typography sx={{ fontSize: 13, color: C.muted }}>
                {new Date(order.created_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                })}
            </Typography>

            {/* Items */}
            <Typography sx={{ fontSize: 13, color: C.text }}>
                {order.items?.length ?? '—'} {order.items?.length === 1 ? 'item' : 'items'}
            </Typography>

            {/* Total */}
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                ${parseFloat(order.total || 0).toFixed(2)}
            </Typography>

            {/* Status */}
            <Pill status={order.status} />

            {/* Action */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    fontSize: 12, fontWeight: 600, color: C.blue,
                    '&:hover': { color: C.blueDark },
                }}>
                    Details <ArrowIcon sx={{ fontSize: 14 }} />
                </Box>
            </Box>
        </Box>
    );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const PurchasesPage = () => {
    const navigate = useNavigate();

    const { data: orders, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['orders'],
        queryFn: marketplaceService.getOrders,
        staleTime: 60 * 1000,
    });

    const firstComponentIds = [...new Set(
        (orders || []).map((o) => o.items?.[0]?.component_id).filter(Boolean)
    )];
    const { data: componentMap = {} } = useQuery({
        queryKey: ['orderListComponents', firstComponentIds],
        queryFn: async () => {
            const results = await Promise.all(
                firstComponentIds.map((id) => marketplaceService.getComponent(id).catch(() => null))
            );
            return Object.fromEntries(results.filter(Boolean).map((c) => [c.id, c]));
        },
        enabled: firstComponentIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const { data: imageMap = {} } = useQuery({
        queryKey: ['orderListImages', firstComponentIds],
        queryFn: async () => {
            const entries = await Promise.all(
                firstComponentIds.map(async (id) => {
                    try {
                        const imgs = await marketplaceService.getComponentImages(id);
                        const primary = imgs?.find(i => i.is_primary) || imgs?.[0];
                        return [id, primary?.image_url || null];
                    } catch { return [id, null]; }
                })
            );
            return Object.fromEntries(entries);
        },
        enabled: firstComponentIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const totalSpent   = (orders || []).reduce((s, o) => s + parseFloat(o.total || 0), 0);
    const activeCount  = (orders || []).filter(o => ['pending', 'confirmed', 'shipped'].includes(o.status)).length;
    const deliveredCount = (orders || []).filter(o => o.status === 'delivered').length;

    return (
        <Box sx={{ bgcolor: C.bg, minHeight: '100vh' }}>
            {/* Page header */}
            <Box sx={{ bgcolor: C.white, borderBottom: `1px solid ${C.border}`, px: 4, py: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: C.radius,
                        bgcolor: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <BagIcon sx={{ fontSize: 20, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
                            My Purchases
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: C.muted }}>
                            Track all your purchases and delivery status
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Content */}
            <Box sx={{ bgcolor: C.white, borderBottom: `1px solid ${C.border}` }}>

                {/* Error */}
                {isError && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error" action={
                            <Button size="small" onClick={() => refetch()}>Retry</Button>
                        }>
                            {error?.message || 'Failed to load purchases'}
                        </Alert>
                    </Box>
                )}

                {/* Loading */}
                {isLoading && (
                    <Box>
                        {/* Stat skeletons */}
                        <Stack direction="row" spacing={2} sx={{ p: 3, borderBottom: `1px solid ${C.border}` }}>
                            {[...Array(3)].map((_, i) => (
                                <Box key={i} sx={{ flex: 1, p: 2.5, border: `1px solid ${C.border}`, borderRadius: C.radius }}>
                                    <Skeleton variant="text" width="50%" height={14} sx={{ mb: 0.5 }} />
                                    <Skeleton variant="text" width="40%" height={28} />
                                </Box>
                            ))}
                        </Stack>
                        {[...Array(4)].map((_, i) => (
                            <Box key={i} sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${C.border}` }}>
                                <Skeleton variant="text" width="35%" height={18} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="20%" height={14} />
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Empty state */}
                {!isLoading && !isError && orders?.length === 0 && (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                        <Box sx={{
                            width: 64, height: 64, borderRadius: '50%', bgcolor: C.blueLight,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                        }}>
                            <ReceiptIcon sx={{ fontSize: 30, color: C.blue }} />
                        </Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.text, mb: 0.5 }}>
                            No purchases yet
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: C.muted, mb: 3 }}>
                            When you buy something, your purchases will appear here.
                        </Typography>
                        <Button
                            variant="contained"
                            endIcon={<ArrowIcon />}
                            onClick={() => navigate('/parts')}
                            sx={{
                                bgcolor: C.blue, borderRadius: C.radius, textTransform: 'none',
                                fontWeight: 600, boxShadow: 'none',
                                '&:hover': { bgcolor: C.blueDark, boxShadow: 'none' },
                            }}
                        >
                            Browse Marketplace
                        </Button>
                    </Box>
                )}

                {/* Loaded state */}
                {!isLoading && orders?.length > 0 && (
                    <>
                        {/* Stats */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                            sx={{ p: 3, borderBottom: `1px solid ${C.border}` }}>
                            <StatCard icon={<SpendIcon />}  label="Total Spent"   value={`$${totalSpent.toFixed(2)}`} />
                            <StatCard icon={<BagIcon />}    label="Total Purchases" value={orders.length} />
                            <StatCard icon={<ShipIcon />}   label="In Progress"   value={activeCount}
                                iconColor="#7b1fa2" iconBg="#f3e5f5" />
                            <StatCard icon={<DoneIcon />}   label="Delivered"     value={deliveredCount}
                                iconColor="#2e7d32" iconBg="#e8f5e9" />
                        </Stack>

                        {/* Table header */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '2.5fr 1.2fr 0.8fr 1fr 1.2fr 100px',
                            px: 3, py: 1.5, bgcolor: C.bg,
                            borderBottom: `1px solid ${C.border}`,
                        }}>
                            {['Purchase', 'Date', 'Items', 'Total', 'Status', ''].map((h, i) => (
                                <Typography key={i} sx={{
                                    fontSize: 11, fontWeight: 700, color: C.muted,
                                    textTransform: 'uppercase', letterSpacing: '0.06em',
                                }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>

                        {/* Rows */}
                        {orders.map((order) => (
                            <PurchaseRow
                                key={order.order_id}
                                order={order}
                                componentMap={componentMap}
                                imageMap={imageMap}
                                onClick={() => navigate(`/orders/${order.order_id}`)}
                            />
                        ))}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PurchasesPage;